import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { Trophy, Calendar, DollarSign, Users, ArrowLeft, TrendingUp, Plus, EyeOff, RefreshCw, Clock, Percent } from 'lucide-react';

function toDateTimeLocalValue(isoOrDbString) {
  if (!isoOrDbString) return '';
  const d = new Date(isoOrDbString);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const AdminGameWeekDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gameweek, setGameweek] = useState(null);
  const [allTournaments, setAllTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prizeBreakdown, setPrizeBreakdown] = useState(null);
  const [hidePrize, setHidePrize] = useState(false);
  const [updatingHidePrize, setUpdatingHidePrize] = useState(false);
  const [allocatedPrize, setAllocatedPrize] = useState(null);
  const [editingPrizes, setEditingPrizes] = useState(false);
  const [prizeDraft, setPrizeDraft] = useState({ allocated: null, tournaments: {} });
  const [savingPrizes, setSavingPrizes] = useState(false);
  const [prizeError, setPrizeError] = useState(null);
  const [recalcModal, setRecalcModal] = useState({ show: false, preview: null, loading: false, executing: false });
  const [prizeRecalcModal, setPrizeRecalcModal] = useState({
    open: false,
    loading: false,
    applying: false,
    data: null,
    error: null
  });
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({ startLocal: '', endLocal: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  useEffect(() => {
    loadGameWeekDetails();
  }, [id]);

  const loadGameWeekDetails = async () => {
    setLoading(true);
    try {
      console.log('Loading gameweek details for ID:', id);
      
      // Get the gameweek by its ID
      const gameweekResponse = await adminApi.getGameWeek(parseInt(id));
      console.log('Gameweek response:', gameweekResponse);
      
      if (gameweekResponse && gameweekResponse.success && gameweekResponse.gameweek) {
        const gameweekData = gameweekResponse.gameweek;
        console.log('Gameweek data:', gameweekData);
        
        // Set the gameweek data (convert to tournament-like format for compatibility)
        setGameweek({
          id: gameweekData.id,
          name: gameweekData.name,
          start_date: gameweekData.startDate,
          end_date: gameweekData.endDate,
          status: gameweekData.status || 'upcoming'
        });
        setHidePrize(!!gameweekData.hidePrize);
        {
          const raw = gameweekData.allocatedPrize;
          const n = raw != null && raw !== '' ? Number(raw) : NaN;
          setAllocatedPrize(Number.isFinite(n) ? n : null);
        }
        
        // Get all tournaments for this gameweek
        const tournaments = (gameweekResponse.tournaments || []).map((tournament) => ({
          ...tournament,
          status: tournament.status || gameweekData.status || 'upcoming',
          prize_pool: parseFloat(tournament.prize_pool || 0)
        }));
        console.log('Found gameweek tournaments:', tournaments.length);
        console.log('Tournaments:', tournaments.map(t => ({ id: t.id, name: t.name, rarity: t.rarity_restriction })));
        setAllTournaments(tournaments);
        
        // Carica il prize breakdown per il primo torneo
        if (tournaments.length > 0) {
          try {
            const breakdown = await adminApi.getGameWeekPrizeBreakdown(tournaments[0].id);
            console.log('Prize breakdown:', breakdown);
            setPrizeBreakdown(breakdown);
          } catch (error) {
            console.error('Error loading prize breakdown:', error);
          }
        }
      } else {
        console.error('Gameweek not found with ID:', id);
      }
    } catch (error) {
      console.error('Error loading gameweek details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/20 text-blue-400';
      case 'open': return 'bg-green-500/20 text-green-400';
      case 'in_progress': return 'bg-green-500/20 text-green-400 border-2 border-green-400';
      case 'closed': return 'bg-gray-500/20 text-gray-400';
      case 'close': return 'bg-gray-500/20 text-gray-400';
      case 'pending_payment': return 'bg-purple-500/20 text-purple-400';
      case 'completed': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress':
        return 'PROGRESS';
      case 'pending_payment':
        return 'PENDING PAYMENT';
      case 'close':
      case 'closed':
        return 'CLOSE';
      case 'upcoming':
        return 'UPCOMING';
      case 'open':
        return 'OPEN';
      default:
        return String(status || '').toUpperCase();
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'basic': return 'bg-gray-500/20 text-gray-400';
      case 'plus': return 'bg-blue-500/20 text-blue-400';
      case 'rare': return 'bg-purple-500/20 text-purple-400';
      case 'epic': return 'bg-orange-500/20 text-orange-400';
      case 'unique': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
        <p className="mt-4 text-gray-400">Loading gameweek details...</p>
      </div>
    );
  }

  if (!gameweek) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p>GameWeek not found</p>
        <button
          onClick={() => navigate('/admin/gameweeks')}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          Back to GameWeeks
        </button>
      </div>
    );
  }

  const gameweekName = gameweek.name.split(' - ')[0];
  const isClosedGameweek = ['close', 'closed', 'completed', 'paid'].includes(
    String(gameweek.status || '').toLowerCase()
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/gameweeks')}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold flex items-center">
            <Trophy className="w-8 h-8 mr-3 text-yellow-400" />
            {gameweekName}
          </h1>
        </div>
        <span className={`px-4 py-2 rounded-lg font-semibold ${getStatusColor(gameweek.status)}`}>
          {getStatusLabel(gameweek.status)}
        </span>
      </div>

      {/* GameWeek Info */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">Start Date</p>
              <p className="text-white font-semibold">
                {new Date(gameweek.start_date).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">End Date</p>
              <p className="text-white font-semibold">
                {new Date(gameweek.end_date).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <DollarSign className="w-5 h-5 text-gray-400" />
            <div className="min-w-0">
              <p className="text-sm text-gray-400">Total Prize Pool (sum)</p>
              <p className="text-yellow-400 font-semibold">
                {allTournaments.reduce((sum, t) => sum + (parseFloat(t.prize_pool) || 0), 0).toFixed(2)} DIOS
              </p>
              <button
                type="button"
                disabled={prizeRecalcModal.loading || allTournaments.length === 0}
                onClick={async () => {
                  setPrizeRecalcModal({ open: true, loading: true, applying: false, data: null, error: null });
                  try {
                    const res = await adminApi.recalculateGameWeekPrizePools(gameweek.id, true);
                    setPrizeRecalcModal((prev) => ({
                      ...prev,
                      loading: false,
                      data: res
                    }));
                  } catch (err) {
                    setPrizeRecalcModal((prev) => ({
                      ...prev,
                      loading: false,
                      error: err.data?.error || err.message || 'Errore'
                    }));
                  }
                }}
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-amber-700/80 hover:bg-amber-600 text-white disabled:opacity-50"
                title="Il 90% dei buy-in resta sul torneo di iscrizione. Solo l'eccedenza viene ripartita tra Plus/Rare/Epic/Unique (13/22/30/35%)."
              >
                <Percent className="w-3.5 h-3.5" />
                Ricalcola montepremi (pesi)
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <DollarSign className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">Allocated Total</p>
              <p className="text-green-400 font-semibold">
                {allocatedPrize != null && Number.isFinite(Number(allocatedPrize))
                  ? `${Number(allocatedPrize).toFixed(2)} DIOS`
                  : 'Auto (from tournaments)'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">Apertura e chiusura</h3>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Modificando data e ora il backend ricalcola lo stato (es. se l&apos;inizio passa nel futuro, la gameweek torna upcoming).
          </p>
          {!editingSchedule ? (
            <button
              type="button"
              onClick={() => {
                setScheduleError(null);
                setScheduleDraft({
                  startLocal: toDateTimeLocalValue(gameweek.start_date),
                  endLocal: toDateTimeLocalValue(gameweek.end_date)
                });
                setEditingSchedule(true);
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
            >
              Modifica orari
            </button>
          ) : (
            <div className="space-y-4 max-w-xl">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Apertura</label>
                <input
                  type="datetime-local"
                  value={scheduleDraft.startLocal}
                  onChange={(e) => setScheduleDraft((p) => ({ ...p, startLocal: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Chiusura</label>
                <input
                  type="datetime-local"
                  value={scheduleDraft.endLocal}
                  onChange={(e) => setScheduleDraft((p) => ({ ...p, endLocal: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>
              {scheduleError && (
                <p className="text-sm text-red-400">{scheduleError}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={savingSchedule || !scheduleDraft.startLocal || !scheduleDraft.endLocal}
                  onClick={async () => {
                    setScheduleError(null);
                    const start = new Date(scheduleDraft.startLocal);
                    const end = new Date(scheduleDraft.endLocal);
                    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                      setScheduleError('Date non valide');
                      return;
                    }
                    if (end.getTime() <= start.getTime()) {
                      setScheduleError("La chiusura deve essere dopo l'apertura");
                      return;
                    }
                    setSavingSchedule(true);
                    try {
                      const [startDatePart, startTimePart] = scheduleDraft.startLocal.split('T');
                      const [endDatePart, endTimePart] = scheduleDraft.endLocal.split('T');
                      await adminApi.updateGameWeekSchedule(gameweek.id, {
                        startDate: startDatePart,
                        endDate: endDatePart,
                        startTime: (startTimePart || '00:00').slice(0, 5),
                        endTime: (endTimePart || '00:00').slice(0, 5)
                      });
                      setEditingSchedule(false);
                      await loadGameWeekDetails();
                    } catch (err) {
                      setScheduleError(err.message || err.data?.error || 'Salvataggio fallito');
                    } finally {
                      setSavingSchedule(false);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white disabled:opacity-50"
                >
                  {savingSchedule ? 'Salvataggio…' : 'Salva orari'}
                </button>
                <button
                  type="button"
                  disabled={savingSchedule}
                  onClick={() => {
                    setEditingSchedule(false);
                    setScheduleError(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recalc Scores & Edit Prizes */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
        <h2 className="text-xl font-bold mb-4">Scores & Prizes</h2>
        {!isClosedGameweek && (
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={async () => {
                setRecalcModal({ show: true, preview: null, loading: true, executing: false });
                try {
                  const res = await adminApi.recalcScoresForGameWeek(gameweek.id, true);
                  const data = res?.data || res;
                  setRecalcModal(prev => ({ ...prev, preview: data, loading: false }));
                } catch (err) {
                  setRecalcModal(prev => ({ ...prev, preview: { success: false, error: err.response?.data?.error || err.message }, loading: false }));
                }
              }}
              disabled={recalcModal.loading}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 flex items-center space-x-2"
              title="Recalculate scores (dry-run first, then confirm)"
            >
              <RefreshCw className="w-4 h-4" />
              Recalculate Scores
            </button>
          </div>
        )}
        <h3 className="text-lg font-semibold mb-2">Edit Prizes</h3>
        <p className="text-gray-400 text-sm mb-4">
          Set allocated total for gameweek and prize per tournament. Sum of tournament prizes must equal allocated total.
        </p>
        {!editingPrizes ? (
          <button
            onClick={() => {
              setEditingPrizes(true);
              setPrizeDraft({
                allocated: allocatedPrize ?? allTournaments.reduce((s, t) => s + (parseFloat(t.prize_pool) || 0), 0),
                tournaments: Object.fromEntries(allTournaments.map(t => [t.id, parseFloat(t.prize_pool) || 0]))
              });
              setPrizeError(null);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            Edit Allocated & Tournament Prizes
          </button>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Allocated Total (DIOS) - leave empty for auto</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Auto from tournaments"
                value={prizeDraft.allocated != null ? prizeDraft.allocated : ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setPrizeDraft(prev => ({ ...prev, allocated: v === '' ? null : parseFloat(v) || 0 }));
                }}
                className="w-48 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {allTournaments.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded">
                  <span className="text-sm text-gray-300 truncate">{t.rarity_restriction || t.name.split(' - ')[1]}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={prizeDraft.tournaments[t.id] ?? 0}
                    onChange={(e) => setPrizeDraft(prev => ({
                      ...prev,
                      tournaments: { ...prev.tournaments, [t.id]: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-24 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-yellow-400 text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  setSavingPrizes(true);
                  setPrizeError(null);
                  try {
                    const allocated = prizeDraft.allocated != null ? prizeDraft.allocated : null;
                    const sum = Object.values(prizeDraft.tournaments).reduce((a, b) => a + b, 0);
                    if (allocated != null && Math.abs(sum - allocated) > 0.01) {
                      setPrizeError(`Sum (${sum.toFixed(2)}) must equal allocated (${allocated.toFixed(2)})`);
                      return;
                    }
                    await adminApi.updateGameWeekPrizes(gameweek.id, {
                      allocatedPrize: allocated,
                      tournamentPrizes: Object.entries(prizeDraft.tournaments).map(([tournamentId, prizePool]) => ({
                        tournamentId: parseInt(tournamentId),
                        prizePool
                      }))
                    });
                    setAllocatedPrize(allocated);
                    setAllTournaments(prev => prev.map(t => ({
                      ...t,
                      prize_pool: prizeDraft.tournaments[t.id] ?? t.prize_pool
                    })));
                    setEditingPrizes(false);
                  } catch (err) {
                    setPrizeError(err.response?.data?.error || err.message);
                  } finally {
                    setSavingPrizes(false);
                  }
                }}
                disabled={savingPrizes}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
              >
                {savingPrizes ? 'Saving...' : 'Save Prizes'}
              </button>
              <button
                onClick={() => { setEditingPrizes(false); setPrizeError(null); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
            {prizeError && <p className="text-red-400 text-sm">{prizeError}</p>}
          </div>
        )}
      </div>

      {/* Tournaments List (5 tornei standard + eventuali tornei personalizzati) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Tournaments</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={async () => {
                if (updatingHidePrize) return;
                setUpdatingHidePrize(true);
                try {
                  const newValue = !hidePrize;
                  await adminApi.updateGameWeekHidePrize(gameweek.id, newValue);
                  setHidePrize(newValue);
                } catch (err) {
                  console.error('Error updating hide prize:', err);
                } finally {
                  setUpdatingHidePrize(false);
                }
              }}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all ${hidePrize ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-600 hover:bg-gray-700'}`}
              title={hidePrize ? 'Prize hidden in /tournaments - click to show' : 'Prize visible in /tournaments - click to hide'}
            >
              <EyeOff className="w-4 h-4" />
              <span>HIDE PRIZE</span>
              {hidePrize && <span className="text-xs">ON</span>}
            </button>
            <button
              onClick={() => {
                // TODO: Aprire modal per aggiungere torneo personalizzato
                console.log('Add tournament to gameweek:', gameweekName);
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Tournament</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allTournaments.map((tournament) => {
            const rarity = tournament.rarity_restriction || 'all';
            return (
              <div
                key={tournament.id}
                className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{tournament.name.split(' - ')[1] || tournament.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRarityColor(rarity)}`}>
                    {rarity.toUpperCase()}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(tournament.status)}`}>
                      {getStatusLabel(tournament.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Buy-in:</span>
                    <span className="text-white font-semibold">
                      {tournament.buy_in_amount || 0} DIOS
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Prize Pool:</span>
                    <span className="text-yellow-400 font-semibold">
                      {tournament.prize_pool || 0} DIOS
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Participants:</span>
                    <span className="text-white font-semibold">
                      {tournament.participants || 0}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/admin/tournaments/${tournament.id}`)}
                  className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
                >
                  View Tournament Details
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Prize Breakdown */}
      {prizeBreakdown && prizeBreakdown.breakdown && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2" />
            Prize Breakdown
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-sm">Total Buy-ins</p>
              <p className="text-white text-xl font-bold">
                {prizeBreakdown.breakdown.totalBuyIns || 0} DIOS
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Prize Pool (90%)</p>
              <p className="text-yellow-400 text-xl font-bold">
                {prizeBreakdown.breakdown.prizePoolFromBuyIns || 0} DIOS
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Guaranteed Prize</p>
              <p className="text-green-400 text-xl font-bold">
                {prizeBreakdown.breakdown.guaranteedPrize || 0} DIOS
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Prize Pool</p>
              <p className="text-yellow-400 text-xl font-bold">
                {prizeBreakdown.breakdown.totalPrizePool || 0} DIOS
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ricalcolo montepremi per pesi rarità */}
      {prizeRecalcModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-600 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Percent className="w-5 h-5 text-amber-400" />
                Ricalcolo montepremi (pesi rarità)
              </h3>
            </div>
            <div className="p-4 overflow-y-auto flex-1 text-sm text-gray-300 space-y-3">
              <p className="text-gray-400">
                Restano sul torneo: <strong className="text-gray-300">90% di ogni buy-in</strong> (da <code className="text-xs">tournament_entries</code>) e l&apos;importo <strong className="text-gray-300">guaranteed_prize</strong>.
                Solo l&apos;<strong className="text-gray-300">eccedenza</strong> del <code className="text-xs">prize_pool</code> rispetto a quella base viene ripartita tra Plus / Rare / Epic / Unique (13% / 22% / 30% / 35%, normalizzati sui tier presenti).
                Basic tiene solo la parte buy-in (di solito 0). Tornei con rarità non standard restano invariati. Il totale gameweek non cambia.
              </p>
              {prizeRecalcModal.loading && <p className="text-gray-400">Anteprima…</p>}
              {prizeRecalcModal.error && <p className="text-red-400">{prizeRecalcModal.error}</p>}
              {!prizeRecalcModal.loading && prizeRecalcModal.data && (
                <>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <span>Totale gameweek: <strong className="text-white">{Number(prizeRecalcModal.data.total).toFixed(2)}</strong> DIOS</span>
                    <span>Eccedenza da ripartire (pool − buy-in 90% − garantito): <strong className="text-white">{Number(prizeRecalcModal.data.redistributeTotal).toFixed(2)}</strong></span>
                  </div>
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-600">
                        <th className="py-2 pr-1">Rarità</th>
                        <th className="py-2 pr-1">Prima</th>
                        <th className="py-2 pr-1">Bloccati</th>
                        <th className="py-2 pr-1">Eccedenza</th>
                        <th className="py-2">Dopo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(prizeRecalcModal.data.rows || []).map((row) => (
                        <tr key={row.tournamentId} className="border-b border-gray-700/50">
                          <td className="py-2 pr-1 capitalize">{row.rarity}</td>
                          <td className="py-2 pr-1 text-gray-400">{Number(row.oldPool).toFixed(2)}</td>
                          <td className="py-2 pr-1 text-blue-300/90">{Number(row.lockedFromBuyIns ?? 0).toFixed(2)}</td>
                          <td className="py-2 pr-1 text-gray-500">{Number(row.excessBefore ?? 0).toFixed(2)}</td>
                          <td className="py-2 text-yellow-400 font-medium">{Number(row.newPool).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-amber-200/90">Anteprima (dry-run): nessuna modifica al DB finché non confermi.</p>
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-2 flex-wrap">
              <button
                type="button"
                disabled={prizeRecalcModal.applying}
                onClick={() => setPrizeRecalcModal({ open: false, loading: false, applying: false, data: null, error: null })}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
              >
                Chiudi
              </button>
              <button
                type="button"
                disabled={
                  prizeRecalcModal.loading ||
                  prizeRecalcModal.applying ||
                  !prizeRecalcModal.data ||
                  prizeRecalcModal.error
                }
                onClick={async () => {
                  setPrizeRecalcModal((p) => ({ ...p, applying: true, error: null }));
                  try {
                    await adminApi.recalculateGameWeekPrizePools(gameweek.id, false);
                    setPrizeRecalcModal({ open: false, loading: false, applying: false, data: null, error: null });
                    await loadGameWeekDetails();
                  } catch (err) {
                    setPrizeRecalcModal((p) => ({
                      ...p,
                      applying: false,
                      error: err.data?.error || err.message || 'Errore salvataggio'
                    }));
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
              >
                {prizeRecalcModal.applying ? 'Salvataggio…' : 'Applica in database'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recalc scores modal */}
      {recalcModal.show && !isClosedGameweek && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-600 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold">Recalculate Scores</h3>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {recalcModal.loading && (
                <p className="text-gray-400">Running dry-run...</p>
              )}
              {!recalcModal.loading && recalcModal.preview && (
                <>
                  {recalcModal.preview.success === false ? (
                    <p className="text-red-400">{recalcModal.preview.error || 'Dry-run failed'}</p>
                  ) : (
                    <>
                      <p className="text-green-400 mb-2">Dry-run complete</p>
                      <pre className="bg-gray-900 p-3 rounded text-sm text-gray-300 whitespace-pre-wrap overflow-x-auto">
                        {recalcModal.preview.output_lines?.join('\n') || JSON.stringify(recalcModal.preview, null, 2)}
                      </pre>
                      <p className="text-gray-400 text-sm mt-2">
                        Teams processed: {recalcModal.preview.teams_processed ?? '-'} / scored: {recalcModal.preview.teams_scored ?? '-'}
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setRecalcModal({ show: false, preview: null, loading: false, executing: false })}
                disabled={recalcModal.executing}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (recalcModal.executing || recalcModal.preview?.success === false) return;
                  setRecalcModal(prev => ({ ...prev, executing: true }));
                  try {
                    const res = await adminApi.recalcScoresForGameWeek(gameweek.id, false);
                    const data = res?.data || res;
                    if (data?.started) {
                      const gwId = gameweek.id;
                      const pollInterval = 2000;
                      const poll = async () => {
                        try {
                          const statusRes = await adminApi.getRecalcScoresStatus(gwId);
                          const statusData = statusRes?.data || statusRes;
                          if (statusData?.status === 'idle') {
                            const result = statusData?.result;
                            setRecalcModal(prev => ({ ...prev, executing: false }));
                            if (result?.success) {
                              alert('Scores recalculated successfully.');
                              setRecalcModal({ show: false, preview: null, loading: false, executing: false });
                              loadGameWeekDetails();
                            } else if (result?.error) {
                              setRecalcModal(prev => ({ ...prev, preview: { success: false, error: result.error } }));
                            } else {
                              alert('Recalc completed.');
                              setRecalcModal({ show: false, preview: null, loading: false, executing: false });
                              loadGameWeekDetails();
                            }
                          } else {
                            setTimeout(poll, pollInterval);
                          }
                        } catch (e) {
                          setRecalcModal(prev => ({ ...prev, executing: false }));
                          alert('Errore polling status: ' + (e?.message || 'Unknown'));
                        }
                      };
                      setTimeout(poll, pollInterval);
                      return;
                    }
                    setRecalcModal(prev => ({ ...prev, preview: data, executing: false }));
                    if (data?.success) {
                      alert('Scores recalculated successfully.');
                      setRecalcModal({ show: false, preview: null, loading: false, executing: false });
                      loadGameWeekDetails();
                    } else {
                      setRecalcModal(prev => ({ ...prev, executing: false }));
                    }
                  } catch (err) {
                    setRecalcModal(prev => ({ ...prev, preview: { success: false, error: err.response?.data?.error || err.message }, executing: false }));
                  }
                }}
                disabled={recalcModal.executing || recalcModal.loading || recalcModal.preview?.success === false}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
              >
                {recalcModal.executing ? 'Running...' : 'Confirm and run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGameWeekDetail;

